import { type FormEvent, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { Field } from '../../components/ui/Field';
import { api } from '../../lib/api';
import { AuthLayout } from './AuthLayout';
import { useFormErrors } from './useFormErrors';

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const errors = useFormErrors();

  const token = params.get('token');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [mismatch, setMismatch] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    errors.clear();

    /*
     * The confirmation field is checked here and nowhere else.
     *
     * The server never receives it - it exists only to catch a typo in a value
     * the person cannot read back on screen. Sending it would mean the API
     * validating a field that is purely a UI affordance.
     */
    if (password !== confirm) {
      setMismatch(true);

      return;
    }

    setMismatch(false);
    setBusy(true);

    try {
      await api.post('/auth/reset-password', { token, password }, { anonymous: true });
      setDone(true);
    } catch (error) {
      errors.capture(error);
    } finally {
      setBusy(false);
    }
  }

  if (!token) {
    return (
      <AuthLayout title="Missing reset link">
        <Alert tone="error">
          Open the link from your email, or{' '}
          <Link to="/forgot-password" className="text-accent hover:underline">
            request a new one
          </Link>
          .
        </Alert>
      </AuthLayout>
    );
  }

  /*
   * Signing in again is deliberate, not an omission.
   *
   * A reset link arrives in a mailbox, and a mailbox is exactly what gets
   * compromised - so the server issues no session here, and proving the new
   * password works is the last step. Every other device was signed out at the
   * same time, which is the point of a reset rather than a side effect.
   */
  if (done) {
    return (
      <AuthLayout
        title="Password changed"
        footer={
          <Link to="/login" className="text-accent hover:underline">
            Sign in
          </Link>
        }
      >
        <Alert tone="success">You have been signed out everywhere else. Sign in with your new password.</Alert>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Choose a new password">
      <form onSubmit={submit} className="flex flex-col gap-4">
        {errors.message && <Alert tone="error">{errors.message}</Alert>}

        <Field
          label="New password"
          type="password"
          name="password"
          autoComplete="new-password"
          autoFocus
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          hint="At least 10 characters."
          error={errors.fields.password}
        />

        <Field
          label="Confirm new password"
          type="password"
          name="password_confirm"
          autoComplete="new-password"
          required
          value={confirm}
          onChange={(e) => {
            setConfirm(e.target.value);
            // Cleared as they correct it, rather than persisting until the next
            // submit - a stale "those do not match" under a field that now
            // matches reads as the form being broken.
            setMismatch(false);
          }}
          error={mismatch ? 'Those do not match.' : undefined}
        />

        <Button type="submit" loading={busy} full>
          Change password
        </Button>
      </form>
    </AuthLayout>
  );
}
